/**
 * Motor SQL in-browser cu DuckDB-WASM.
 * 
 * Aplatizează cuburile Zarr (3D) într-un tabel columnar,
 * îl înregistrează în DuckDB-WASM, și permite query-uri SQL
 * pentru crossfiltering și statistici populație.
 */
import * as duckdb from '@duckdb/duckdb-wasm';

// Vite ?url imports — forțează Vite să copieze fișierele WASM/worker în dist
import duckdb_wasm from '@duckdb/duckdb-wasm/dist/duckdb-mvp.wasm?url';
import mvp_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-mvp.worker.js?url';
import duckdb_wasm_eh from '@duckdb/duckdb-wasm/dist/duckdb-eh.wasm?url';
import eh_worker from '@duckdb/duckdb-wasm/dist/duckdb-browser-eh.worker.js?url';

// ── State ────────────────────────────────────────────────────────
let db = null;
let conn = null;
let initialized = false;

/** Metadate despre grila CAMS (populate la înregistrare) */
let gridMeta = { nlat: 0, nlon: 0, lats: null, lons: null };

/** Date populație, vector plat (id -> pop_total) pentru randare ultra-rapidă */
export let popArray = new Uint32Array(672800);

// ── Init ─────────────────────────────────────────────────────────

/**
 * Inițializează DuckDB-WASM.
 * Trebuie apelat o singură dată, la boot.
 */
export async function initSQL() {
  if (initialized) return;

  console.time('[SQL] Init DuckDB-WASM');

  // Bundluri WASM cu path-uri rezolvate de Vite
  const MANUAL_BUNDLES = {
    mvp: {
      mainModule: duckdb_wasm,
      mainWorker: mvp_worker,
    },
    eh: {
      mainModule: duckdb_wasm_eh,
      mainWorker: eh_worker,
    },
  };

  const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
  const logger = new duckdb.ConsoleLogger();
  const worker = new Worker(bundle.mainWorker);
  db = new duckdb.AsyncDuckDB(logger, worker);
  await db.instantiate(bundle.mainModule);
  conn = await db.connect();

  initialized = true;
  console.timeEnd('[SQL] Init DuckDB-WASM');
}

// ── Încărcare date populație ─────────────────────────────────────

/**
 * Încarcă datele de populație pre-agregate (population_1km.parquet)
 * în DuckDB și construiește vectorul plat `popArray`.
 */
export async function loadPopulation() {
  if (!initialized) throw new Error('SQL engine not initialized');

  const url = `${window.location.origin}${import.meta.env.BASE_URL}data/population_1km.parquet`;
  
  console.log(`[SQL] Încărcare Parquet din: ${url}`);
  
  // Fetch Parquet ca ArrayBuffer și înregistrare ca fișier virtual în DuckDB
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to fetch population Parquet: ${resp.status}`);
  const buffer = await resp.arrayBuffer();
  await db.registerFileBuffer('population_1km.parquet', new Uint8Array(buffer));
  
  // Creare tabel din fișierul virtual
  await conn.query(`
    CREATE TABLE IF NOT EXISTS population AS 
    SELECT * FROM read_parquet('population_1km.parquet')
  `);

  // Creare view pentru interpolare biliniară 
  // Mapăm coordonatele sub-grid (1km) la grid-ul CAMS (10km)
  // Centrele CAMS sunt la sub-index 4.5, 14.5, 24.5, etc.
  await conn.query(`
    CREATE OR REPLACE VIEW pop_interp AS
    SELECT 
      id,
      GREATEST(0, LEAST(115, CAST(FLOOR((sub_lon_idx - 4.5)/10.0) AS INTEGER))) as x0,
      GREATEST(0, LEAST(115, CAST(FLOOR((sub_lon_idx - 4.5)/10.0) + 1 AS INTEGER))) as x1,
      GREATEST(0, LEAST(57, CAST(FLOOR((sub_lat_idx - 4.5)/10.0) AS INTEGER))) as y0,
      GREATEST(0, LEAST(57, CAST(FLOOR((sub_lat_idx - 4.5)/10.0) + 1 AS INTEGER))) as y1,
      ((sub_lon_idx - 4.5)/10.0) - FLOOR((sub_lon_idx - 4.5)/10.0) as dx,
      ((sub_lat_idx - 4.5)/10.0) - FLOOR((sub_lat_idx - 4.5)/10.0) as dy
    FROM population
  `);

  // Preluare ID-uri și pop_total pentru harta 1km
  const res = await query('SELECT id, pop_total FROM population');
  popArray.fill(0); // Reset
  let totalPop = 0;
  for (const row of res) {
    const id = Number(row.id);
    const pop = Number(row.pop_total);
    if (id >= 0 && id < popArray.length) {
      popArray[id] = pop;
      totalPop += pop;
    }
  }

  console.log(`[SQL] Populație 1km încărcată: ${totalPop.toLocaleString()} persoane, ${res.length} celule 1km`);
  return { total_population: totalPop, cells: res.length };
}

/**
 * Obține populația pentru o celulă 1km.
 * @param {number} subLatIdx
 * @param {number} subLonIdx
 * @returns {number}
 */
export function getPopulationCount(subLatIdx, subLonIdx) {
  const id = subLatIdx * 1160 + subLonIdx;
  return popArray[id] || 0;
}

// ── Înregistrare cuburi ──────────────────────────────────────────

export async function registerCubeSlice(tableName, cube, timeIndex) {
  // OBSOLET: Această funcție era folosită pentru grid-ul de 10km individual.
  // Nu o mai folosim direct cu populația.
}

/**
 * Înregistrează tabel complet multi-variabilă pentru crossfiltering.
 * Un singur tabel cu toate variabilele AQ + meteo pentru un timestep.
 * 
 * @param {Object<string, object>} cubes - Map variabilă → cube
 * @param {number} timeIndex - Indexul temporal
 * @param {string[]} variables - Lista de variabile de inclus
 */
export async function registerCrossfilterTable(cubes, timeIndex, variables) {
  if (!initialized) throw new Error('SQL engine not initialized');

  // Folosim primul cub ca referință pentru grila
  const refCube = cubes[variables[0]];
  const [nt, ny, nx] = refCube.shape;
  gridMeta = { nlat: ny, nlon: nx, lats: refCube.lats, lons: refCube.lons };

  const totalCells = ny * nx;
  
  // Header
  const varCols = variables.join(',');
  const header = `cams_lat_idx,cams_lon_idx,${varCols}`;
  
  // Creăm un CSV mic doar pentru CAMS (6728 rânduri)
  const rows = new Array(totalCells);
  for (let y = 0; y < ny; y++) {
    for (let x = 0; x < nx; x++) {
      const idx = y * nx + x;
      let row = `${y},${x}`;
      
      for (const v of variables) {
        const cube = cubes[v];
        if (!cube) {
          row += ',NULL';
          continue;
        }
        const [cnt] = cube.shape;
        const ti = Math.min(timeIndex, cnt - 1);
        const val = cube.data[ti * ny * nx + idx];
        row += `,${isNaN(val) ? 'NULL' : val.toFixed(2)}`;
      }
      rows[idx] = row;
    }
  }

  await insertFromCSV('cams_forecast', header, rows);

  await conn.query(`
    CREATE OR REPLACE VIEW grid_1km AS
    SELECT 
      (j.y * 1160 + i.x) AS id,
      j.y AS sub_lat_idx,
      i.x AS sub_lon_idx
    FROM 
      (SELECT unnest(generate_series(0, 579)) AS y) j,
      (SELECT unnest(generate_series(0, 1159)) AS x) i
  `);

  await conn.query(`
    CREATE OR REPLACE VIEW grid_interp AS
    SELECT 
      id,
      GREATEST(0, LEAST(115, CAST(FLOOR((sub_lon_idx - 4.5)/10.0) AS INTEGER))) as x0,
      GREATEST(0, LEAST(115, CAST(CEIL((sub_lon_idx - 4.5)/10.0) AS INTEGER))) as x1,
      ((sub_lon_idx - 4.5)/10.0) - GREATEST(0, LEAST(115, CAST(FLOOR((sub_lon_idx - 4.5)/10.0) AS INTEGER))) as dx,
      GREATEST(0, LEAST(57, CAST(FLOOR((sub_lat_idx - 4.5)/10.0) AS INTEGER))) as y0,
      GREATEST(0, LEAST(57, CAST(CEIL((sub_lat_idx - 4.5)/10.0) AS INTEGER))) as y1,
      ((sub_lat_idx - 4.5)/10.0) - GREATEST(0, LEAST(57, CAST(FLOOR((sub_lat_idx - 4.5)/10.0) AS INTEGER))) as dy
    FROM grid_1km
  `);

  const selectCols = variables.map(v => `
    (
      (c00.${v} * (1 - p.dx) + c10.${v} * p.dx) * (1 - p.dy) +
      (c01.${v} * (1 - p.dx) + c11.${v} * p.dx) * p.dy
    ) AS ${v}
  `).join(',\n      ');

  await conn.query(`DROP TABLE IF EXISTS cells`);
  await conn.query(`
    CREATE TABLE cells AS
    SELECT 
      p.id,
      pop_orig.pop_total,
      pop_orig.pop_lt15,
      pop_orig.pop_15_64,
      pop_orig.pop_ge65,
      pop_orig.pop_female,
      pop_orig.pop_male,
      pop_orig.pop_employed,
      ${selectCols}
    FROM grid_interp p
    LEFT JOIN population pop_orig ON pop_orig.id = p.id
    LEFT JOIN cams_forecast c00 ON p.y0 = c00.cams_lat_idx AND p.x0 = c00.cams_lon_idx
    LEFT JOIN cams_forecast c10 ON p.y0 = c10.cams_lat_idx AND p.x1 = c10.cams_lon_idx
    LEFT JOIN cams_forecast c01 ON p.y1 = c01.cams_lat_idx AND p.x0 = c01.cams_lon_idx
    LEFT JOIN cams_forecast c11 ON p.y1 = c11.cams_lat_idx AND p.x1 = c11.cams_lon_idx
  `);
}

/**
 * Inserție directă din CSV (fără data URL — mai robust).
 */
async function insertFromCSV(tableName, header, rows) {
  const cols = header.split(',');
  const colDefs = cols.map(c => {
    if (['cams_lat_idx', 'cams_lon_idx'].includes(c)) return `${c} INTEGER`;
    return `${c} FLOAT`;
  }).join(', ');

  await conn.query(`DROP TABLE IF EXISTS ${tableName}`);
  await conn.query(`CREATE TABLE ${tableName} (${colDefs})`);

  // Insert batch
  const BATCH_SIZE = 1000;
  for (let i = 0; i < rows.length; i += BATCH_SIZE) {
    const batch = rows.slice(i, i + BATCH_SIZE);
    const values = batch.map(r => `(${r})`).join(',');
    await conn.query(`INSERT INTO ${tableName} VALUES ${values}`);
  }
}

// ── Query ────────────────────────────────────────────────────────

/**
 * Execută un query SQL și returnează rezultatul.
 * @param {string} sql
 * @returns {Array<Object>} - Array de obiecte {col: val, ...}
 */
export async function query(sql) {
  if (!initialized) throw new Error('SQL engine not initialized');
  
  const result = await conn.query(sql);
  return arrowToObjects(result);
}

/**
 * Execută un query SQL și returnează rezultatul ca Arrow Table.
 * @param {string} sql
 * @returns {import('apache-arrow').Table}
 */
export async function queryRaw(sql) {
  if (!initialized) throw new Error('SQL engine not initialized');
  return conn.query(sql);
}

// ── Helpers ──────────────────────────────────────────────────────

/**
 * Convertește un Arrow Table în array de obiecte plain JS.
 */
function arrowToObjects(table) {
  const results = [];
  const schema = table.schema.fields;
  
  for (let i = 0; i < table.numRows; i++) {
    const row = {};
    for (const field of schema) {
      const col = table.getChild(field.name);
      row[field.name] = col.get(i);
    }
    results.push(row);
  }
  return results;
}

/**
 * Convertește CSV string la data URL.
 */
function csvToDataUrl(csv) {
  return `data:text/csv;base64,${btoa(csv)}`;
}

/**
 * Returnează metadatele grilei.
 */
export function getGridMeta() {
  return gridMeta;
}

/**
 * Returnează vectorul de populație (compat. legacy).
 */
export function getPopulationMap() {
  return popArray;
}
